import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import DateRangePicker from '@/components/shared/DateRangePicker';

const GridDateValuePicker = ({ operator, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    // Format the selected date label beautifully like a spreadsheet cell
    let selectedLabel = 'Select or type a date...';
    if (value) {
        const startD = value.start ? (value.start instanceof Date ? value.start : new Date(value.start)) : null;
        const endD = value.end ? (value.end instanceof Date ? value.end : new Date(value.end)) : null;
        if (startD && !isNaN(startD.getTime())) {
            const currentYear = new Date().getFullYear();
            const formatStr = (d, includeYear) => d.toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', ...(includeYear ? { year: 'numeric' } : {})
            });

            if (endD && !isNaN(endD.getTime()) && endD.getTime() !== startD.getTime()) {
                const sameYear = startD.getFullYear() === endD.getFullYear();
                const isCurrentYear = startD.getFullYear() === currentYear && endD.getFullYear() === currentYear;

                if (isCurrentYear) {
                    selectedLabel = `${formatStr(startD, false)} → ${formatStr(endD, false)}`;
                } else if (sameYear) {
                    selectedLabel = `${formatStr(startD, false)} → ${formatStr(endD, true)}`;
                } else {
                    selectedLabel = `${formatStr(startD, true)} → ${formatStr(endD, true)}`;
                }
            } else {
                const isCurrentYear = startD.getFullYear() === currentYear;
                selectedLabel = formatStr(startD, !isCurrentYear);
            }
        }
    }

    return (
        <div ref={containerRef} className="relative w-full flex-1">
            <div 
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`h-7 rounded px-2 bg-[var(--bg-subtle)] text-[13px] font-normal cursor-pointer outline-none flex items-center justify-between w-full relative transition-colors hover:bg-[var(--bg-muted)] ${
                    value?.start ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'
                } ${
                    isOpen || isHovered ? 'border border-[var(--border-strong)]' : 'border border-[var(--border)]'
                }`}
            >
                <span className="truncate">
                    {selectedLabel}
                </span>
                <ObliqueIcon name="keyboard_arrow_down" size={16} color="var(--text-tertiary)" />
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-[calc(100%+4px)] right-0 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-2xl z-[3000] overflow-hidden"
                    >
                        <DateRangePicker 
                            startDate={value?.start || null} 
                            endDate={value?.end || null} 
                            onRangeChange={(start, end) => {
                                onChange({ start, end });
                            }} 
                            embedded={true}
                            compact={false}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default GridDateValuePicker;
